import { useCallback, useEffect, useState } from "react";
import {
	Activity,
	Bot,
	ChevronDown,
	Eye,
	EyeOff,
	Loader2,
	ShieldAlert,
	Target,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { fetchAPI } from "../../lib/utils";
import { AgentChatSheet } from "../ai/AgentChatSheet";
import { PerformanceModelEditor } from "../performance/PerformanceModelEditor";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "../ui/collapsible";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

/**
 * Pannello admin «Profilo Prestativo»: tutti i modelli di uno sport.
 *
 * Prima esisteva solo il modello di sistema, e questo tab era il suo editor.
 * Con i modelli dei coach (docs/sport-config, Fase 2) l'admin deve **vedere**
 * tutto per dare supporto: per ogni sport il modello di sistema e quelli dei
 * coach, pubblicati o no, con quanti atleti li usano e quante scelte a mano li
 * indicano. Apre qualunque modello nello stesso editor dei coach, e su quelli
 * dei coach puo' intervenire (anche pubblicare o ritirare) per supporto.
 */
export function PerformanceModelsAdminTab() {
	const { t } = useLanguage();
	const [sports, setSports] = useState([]);
	const [sport, setSport] = useState("");
	const [overview, setOverview] = useState(null);
	const [loadingOverview, setLoadingOverview] = useState(false);
	const [selectedModelId, setSelectedModelId] = useState(null);
	const [detail, setDetail] = useState(null);
	const [assistantOpen, setAssistantOpen] = useState(false);
	// Dopo una scrittura dell'assistente l'editor rilegge il modello.
	const [editorVersion, setEditorVersion] = useState(0);
	const [publishing, setPublishing] = useState(false);

	useEffect(() => {
		fetchAPI("/admin/sports")
			.then((data) => {
				const active = (Array.isArray(data) ? data : []).filter(
					(item) => item.is_active !== false,
				);
				setSports(active);
				setSport((current) => current || active[0]?.value || "");
			})
			.catch(() => setSports([]));
	}, []);

	const loadOverview = useCallback(async () => {
		if (!sport) return;
		setLoadingOverview(true);
		try {
			const data = await fetchAPI(
				`/admin/performance-models?sport=${encodeURIComponent(sport)}`,
			);
			setOverview(data);
			setSelectedModelId((current) => {
				const ids = (data?.models || []).map((model) => model.model_id);
				return current && ids.includes(current) ? current : ids[0] || null;
			});
		} catch (error) {
			setOverview(null);
			toast.error(`${t("error")}: ${error?.message || ""}`);
		} finally {
			setLoadingOverview(false);
		}
	}, [sport, t]);

	useEffect(() => {
		setSelectedModelId(null);
		setDetail(null);
		loadOverview();
	}, [loadOverview]);

	useEffect(() => {
		if (!selectedModelId) {
			setDetail(null);
			return undefined;
		}
		let cancelled = false;
		fetchAPI(`/performance-models/${encodeURIComponent(selectedModelId)}`)
			.then((data) => {
				if (!cancelled) setDetail(data);
			})
			.catch(() => {
				if (!cancelled) setDetail(null);
			});
		return () => {
			cancelled = true;
		};
	}, [selectedModelId]);

	const togglePublished = async () => {
		if (!detail || detail.is_system) return;
		setPublishing(true);
		try {
			const updated = await fetchAPI(
				`/performance-models/${encodeURIComponent(detail.model_id)}`,
				{
					method: "PUT",
					body: JSON.stringify({ is_published: !detail.is_published }),
				},
			);
			setDetail((prev) => ({ ...prev, ...updated }));
			toast.success(
				updated.is_published ? t("scModelPublished") : t("scModelWithdrawn"),
			);
			await loadOverview();
		} catch (error) {
			toast.error(`${t("error")}: ${error?.message || ""}`);
		} finally {
			setPublishing(false);
		}
	};

	return (
		<div className="space-y-6">
			<Card className="bg-card border-border rounded-sm">
				<CardContent className="p-4 grid grid-cols-1 md:grid-cols-[180px,1fr] items-center gap-3">
					<p className="label-tactical">{t("adminPmSport")}</p>
					<Select value={sport || "__none__"} onValueChange={setSport}>
						<SelectTrigger className="bg-secondary border-transparent rounded-sm">
							<SelectValue placeholder={t("adminPmSelectSport")} />
						</SelectTrigger>
						<SelectContent className="bg-card border-border">
							<SelectItem value="__none__" disabled>
								{t("adminPmSelectSport")}
							</SelectItem>
							{sports.map((item) => (
								<SelectItem key={item.sport_id || item.value} value={item.value}>
									{item.label || item.value}
									{/* Gli sport creati dai coach (Fase 3): di chi sono. */}
									{item.library_scope === "personal" && item.owner?.name
										? ` · ${item.owner.name}`
										: ""}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</CardContent>
			</Card>

			<PerformanceRationale />

			<Card className="bg-card border-border rounded-sm">
				<CardHeader className="pb-2">
					<CardTitle className="font-heading text-xl flex items-center gap-2">
						<Target className="w-5 h-5 text-volt" />
						{t("adminPmModelsTitle")}
					</CardTitle>
					<p className="text-sm text-muted-foreground">
						{t("adminPmModelsHint")}
					</p>
				</CardHeader>
				<CardContent>
					{loadingOverview && !overview ? (
						<div className="flex items-center gap-2 py-6 text-muted-foreground">
							<Loader2 className="w-4 h-4 animate-spin" />
							{t("loading")}
						</div>
					) : (
						<div className="overflow-x-auto border border-border rounded-sm">
							<table className="w-full min-w-[640px] text-sm">
								<thead>
									<tr className="bg-secondary/60 border-b border-border text-left">
										<th className="px-3 py-2 font-medium">{t("adminPmColModel")}</th>
										<th className="px-3 py-2 font-medium">{t("adminPmColOwner")}</th>
										<th className="px-3 py-2 font-medium">{t("adminPmColStatus")}</th>
										<th className="px-3 py-2 font-medium">{t("adminPmColContent")}</th>
										<th className="px-3 py-2 font-medium">{t("adminPmColAthletes")}</th>
									</tr>
								</thead>
								<tbody>
									{(overview?.models || []).map((model) => {
										const active = model.model_id === selectedModelId;
										return (
											<tr
												key={model.model_id}
												onClick={() => setSelectedModelId(model.model_id)}
												className={`border-b border-border/60 last:border-b-0 cursor-pointer ${
													active ? "bg-volt/10" : "hover:bg-secondary/40"
												}`}
											>
												<td className="px-3 py-2 font-medium">
													{model.is_system ? t("scSystemModel") : model.name}
												</td>
												<td className="px-3 py-2 text-muted-foreground">
													{model.owner?.name || t("adminPmPlatform")}
												</td>
												<td className="px-3 py-2">
													<Badge
														className={`border-0 ${
															model.is_published
																? "bg-green-500/20 text-green-400"
																: "bg-secondary text-muted-foreground"
														}`}
													>
														{model.is_published ? t("scPublished") : t("scNotPublished")}
													</Badge>
												</td>
												<td className="px-3 py-2 text-muted-foreground">
													{t("scCounts")
														.replace("{domains}", model.counts?.domains ?? 0)
														.replace("{exercises}", model.counts?.exercises ?? 0)}
												</td>
												<td className="px-3 py-2 text-muted-foreground">
													{t("adminPmAthletesUsing")
														.replace("{count}", model.athletes_using ?? 0)
														.replace("{manual}", model.manual_choices ?? 0)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
					{overview && (
						<p className="mt-2 text-xs text-muted-foreground">
							{t("adminPmAthletesTotal").replace("{count}", overview.athletes ?? 0)}
						</p>
					)}
				</CardContent>
			</Card>

			{detail && (
				<div className="space-y-3">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<div className="min-w-0">
							<h3 className="font-heading text-lg">
								{detail.is_system ? t("scSystemModel") : detail.name}
							</h3>
							{detail.owner && (
								<p className="text-xs text-muted-foreground">
									{t("scModelOf").replace("{name}", detail.owner.name)}
								</p>
							)}
						</div>
						<div className="flex flex-wrap items-center gap-2">
						<Button
							variant="outline"
							size="sm"
							className="rounded-sm border-volt/40 text-volt hover:bg-volt/10"
							onClick={() => setAssistantOpen(true)}
						>
							<Bot className="w-4 h-4 mr-1.5" />
							{detail.can_edit ? t("scAskAssistant") : t("scAskAssistantRead")}
						</Button>
						{!detail.is_system && (
							<Button
								variant="outline"
								size="sm"
								className="rounded-sm"
								disabled={publishing}
								onClick={togglePublished}
							>
								{publishing ? (
									<Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
								) : detail.is_published ? (
									<EyeOff className="w-4 h-4 mr-1.5" />
								) : (
									<Eye className="w-4 h-4 mr-1.5" />
								)}
								{detail.is_published ? t("scWithdraw") : t("scPublish")}
							</Button>
						)}
						</div>
					</div>
					{!detail.is_system && (
						<div className="flex items-start gap-2 rounded-sm border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
							<ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
							<span>{t("adminPmCoachModelWarning")}</span>
						</div>
					)}
					<AgentChatSheet
						open={assistantOpen}
						onOpenChange={setAssistantOpen}
						title={t("scAssistantTitle").replace(
							"{model}",
							detail.is_system ? t("scSystemModel") : detail.name,
						)}
						description={
							detail.can_edit
								? t("scAssistantDescription")
								: t("scAssistantDescriptionRead")
						}
						modelId={detail.model_id}
						onModelsChanged={() => {
							fetchAPI(`/performance-models/${encodeURIComponent(detail.model_id)}`)
								.then((data) => setDetail(data))
								.catch(() => {});
							setEditorVersion((version) => version + 1);
							loadOverview();
						}}
					/>
					<PerformanceModelEditor
						key={`${detail.model_id}_${editorVersion}`}
						model={detail}
						canEdit={!!detail.can_edit}
						sportIsShooting={!!detail.sport_is_shooting}
						onModelChanged={(updated) =>
							setDetail((prev) => ({ ...prev, ...updated }))
						}
						onContentChanged={loadOverview}
					/>
				</div>
			)}
		</div>
	);
}

/**
 * Come si calcolano livelli e 1RM: documentazione per chi amministra, in sola
 * lettura. Spostata qui dal vecchio tab, e corretta sul punto 3: per le unita'
 * diverse dai kg il manuale **non** ha priorita' assoluta, si tiene il migliore
 * fra manuale e agenda (`_build_performance_profile_for_athlete`).
 */
function PerformanceRationale() {
	const [open, setOpen] = useState(false);
	return (
		<Collapsible open={open} onOpenChange={setOpen}>
			<Card className="bg-card border-border rounded-sm">
				<CollapsibleTrigger asChild>
					<button type="button" className="w-full text-left">
						<CardHeader className="pb-2">
							<CardTitle className="font-heading text-base flex items-center gap-2">
								<Activity className="w-4 h-4 text-volt" />
								<span className="flex-1">
									Razionale calcolo performance e 1RM (sola lettura)
								</span>
								<ChevronDown
									className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
								/>
							</CardTitle>
						</CardHeader>
					</button>
				</CollapsibleTrigger>
				<CollapsibleContent>
					<CardContent className="space-y-4 text-sm text-muted-foreground">
						<div className="space-y-1.5">
							<p className="text-foreground font-medium">
								1) Salvataggio risultati da agenda
							</p>
							<p>
								I risultati di agenda confluiscono in{" "}
								<span className="font-mono text-foreground">workout_results</span>.
								Sono inclusi sia i risultati diretti che quelli registrati dentro
								superset (campo{" "}
								<span className="font-mono text-foreground">superset_details</span>
								). Agenda e dashboard atleta confluiscono nello stesso flusso di
								salvataggio.
							</p>
						</div>
						<div className="space-y-1.5">
							<p className="text-foreground font-medium">
								2) Algoritmo 1RM per esercizi in kg (unificato)
							</p>
							<p>
								Il calcolo è identico in tutto il sistema (profilo prestativo,
								suggerimenti carico, classifiche). Il servizio condiviso è{" "}
								<span className="font-mono text-foreground">
									backend/services/one_rm.py
								</span>
								.
							</p>
							<ul className="list-disc pl-5 space-y-1">
								<li>
									Per ogni risultato con{" "}
									<span className="font-mono text-foreground">weight_kg &gt; 0</span>{" "}
									si calcola la stima 1RM con la formula di Epley:{" "}
									<span className="font-mono text-foreground">
										1RM = peso × (1 + reps/30)
									</span>
									, con le ripetizioni limitate fra 1 e 30.
								</li>
								<li>Si prende la stima più alta fra tutti i risultati in agenda.</li>
								<li>
									Con una sola ripetizione la stima coincide con il peso: è
									etichettata come <span className="text-foreground">Test 1RM</span>.
								</li>
								<li>
									La stima da agenda si confronta con il valore manuale e{" "}
									<span className="text-foreground">vale il più alto dei due</span>.
									Esempio: manuale 162 kg, stima da 4×160 kg circa 181 kg: si mostra
									181 kg con l'etichetta «Stima superiore al valore manuale».
								</li>
							</ul>
						</div>
						<div className="space-y-1.5">
							<p className="text-foreground font-medium">
								3) Le altre unità (ripetizioni, tempi, metri, punti, hit factor)
							</p>
							<ul className="list-disc pl-5 space-y-1">
								<li>ripetizioni: il massimo</li>
								<li>secondi e minuti: il tempo minimo</li>
								<li>metri, punti e hit factor: il valore più alto</li>
								<li>
									Anche qui si confronta il valore manuale con quello dell'agenda e
									vale il migliore, nel verso dell'unità.
								</li>
							</ul>
						</div>
						<div className="space-y-1.5">
							<p className="text-foreground font-medium">4) Quale modello vale</p>
							<p>
								Per ogni atleta e sport: la scelta fatta a mano, poi l'unico modello
								pubblicato fra i coach che lo seguono, altrimenti quello di sistema
								(docs/sport-config/DECISIONS.md, D2).
							</p>
						</div>
						<div className="text-xs border-t border-border pt-3">
							Questa sezione è informativa: nessuna impostazione viene modificata da
							qui.
						</div>
					</CardContent>
				</CollapsibleContent>
			</Card>
		</Collapsible>
	);
}
